import { Box, Collapse, Grid, ListItemIcon, MenuItem, Typography, useMediaQuery, useTheme } from "@mui/material";
import Header from "../../common/Header";
import { useEffect, useMemo, useRef, useState } from "react";
import Card from "../../common/Card";
import { useForm } from "react-hook-form";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import { getPracticeListByUserRoleAgreementSlice } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { fetchDocumentById, PracticeInfo, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { hideElement, sortNames } from "../../../utils/Utility";
import { AppRegistrationOutlined, CloudUpload, DoDisturbAltOutlined, DownloadOutlined, ExitToApp, FormatItalicOutlined, KeyboardArrowDown, ModeOutlined, PictureAsPdfOutlined, SearchOutlined, VisibilityOutlined } from "@mui/icons-material";
import Button from "../../common/Button";
import LegacyToolBar from "../../common/LegacyToolBar";
import Table, { Row } from "../../common/CustomTable";
import { AgencyInfo, Column, ManufacturerInfo, showHideLoadingDialog } from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { fetchActiveBillersUserSlice, fetchAgenciesListUserSlice, fetchAllUserRoles, fetchManufacturersListUserSlice, getPracticesByUserRoleUserSlice, UserRole, useUserReducer } from "../../../redux/slice/userSlice";
import { selectIsValueFoundInToken, selectTokenValues, useAuthSlice } from "../../../redux/slice/authSlice";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { AGENCY_USER, BAA_ACTIONS, BAA_STATUS, CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, MANUFACTURE_USER, PRACTICE_USER, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import CustomTextField from "../../common/CustomTextField";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { BaaDetailDTO, previewAgreement, searchBaaDetails, SearchBaaRequest, updateElementInBusinessAgreement, useCreateBusinessAgreementSlice } from "../../../redux/slice/baa/createBusinessAgreementSlice";
import { createListSort } from "../../../utils/listSort";
import { BillerInfo } from "../../../redux/slice/practice/practiceApproveSlice";
import { useLocation, useNavigate } from "react-router-dom";
import PDFViewer from "../../common/pdf/PDFViewer";
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import ExportToExcel from "../../common/ExportToExcel";
import StyledMenu from "../../common/StyledMenu";
import { useModalStyles } from "../../common/useModalStyles";


type Option = {
    value: string | number,
    label: string
};

const columns: Column[] = [
    { id: "email", label: "Email", minWidth: 100 },
    { id: "userType", label: "User Type", minWidth: 100 },
    { id: "practiceName", label: "Practice", minWidth: 100 },
    { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
    { id: "agreementType", label: "Agreement Type", minWidth: 100 },
    { id: "userSignedDate", label: "Signed Date", minWidth: 100 }, //CS-1696
    { id: "agreementStatus", label: "Status", minWidth: 100 },
    { id: "action", label: "Action", minWidth: 150 }
];

const baaListSort = createListSort(
    ["email", "userType", "practiceName", "inactiveDate", "agreementType", "userSignedDate", "agreementStatus"] as const,
    {
        email: "email",
        userType: "userType",
        practiceName: "practiceName",
        inactiveDate: "inactiveDate",
        agreementType: "agreementType",
        userSignedDate: "userSignedDate",
        agreementStatus: "agreementStatus",
    }
);

const PRACTICE_ENTITY: string = "Practice";
const AGENCY_ENTITY: string = "Agency";
const MANUFACTURER_ENTITY: string = "Manufacturer";
const BILLER_ENTITY: string = "Biller";
const NONE_ENTITY: string = "None";

const orderedUserList = [
    "Account Representative",
    "Admin",
    "All Agency Users",
    "Agency Admin",
    "Agency Staff",
    "Biller",
    "All Manufacturer Users",
    "Manufacturer Admin",
    "Manufacturer IVR",
    "Manufacturer Service",
    "Manufacturer Staff",
    "All Practice Users",
    "Practice Admin",
    "Practice Staff",
    "Provider"
];

const ENTITY_MERGE_MAP: Record<string, string> = {
    ACCOUNTEXECUTIVE: "AGENCY",
    PROVIDER: "PRACTICE",
};

const getNormalizedEntity = (entity: string | undefined | null): string | null => {
    if (!entity) return null;
    const upperEntity = entity.toUpperCase();
    return ENTITY_MERGE_MAP[upperEntity] || upperEntity;
};

const toRoleEntityDisplayName = (entity: string): string => {
    const normalized = getNormalizedEntity(entity);
    if (normalized === "AGENCY") return AGENCY_ENTITY;
    if (normalized === "MANUFACTURER") return MANUFACTURER_ENTITY;
    if (normalized === "PRACTICE") return PRACTICE_ENTITY;
    if (normalized === "BILLER") return BILLER_ENTITY;
    return entity;
};

export type SearchBaaFields = {
    userRoleId?: number | string;
    entityIds: number[];
    extraRoleIds?: number[];
    firstName: string;
    lastName: string;
    status: string[];
    practiceIds?: number[];
    manufactureIds?:number[];
    agencyIds?: number[];
    billerIds?: number[];
    email?: string; //CS-1696
}

const BaaList: React.FC = () => {
    const ITEMS_PER_PAGE = 10;
    const [showCard, setShowCard] = useState(false);
    const {
      filterPanelRef,
      toggleFilterPanel,
      handleFilterPanelEntering,
      handleFilterPanelEntered,
      handleFilterPanelExited,
    } = useFilterPanelFocus(showCard, setShowCard);
    const { control, handleSubmit, reset, setValue, getValues, watch } = useForm<SearchBaaFields>();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const dispatch = useAppDispatch();
    const { navigate } = useNavigation();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const managingUserRoles = useSelector((state: RootState) => selectTokenValues("canManageUsers")(state));
    const { userInfo } = useAuthSlice();
    const { primaryBtnSx, tokens: modalTokens } = useModalStyles();
    const loggedInUserRole = userInfo.userRole;
    const [loggedInRoleEntity, setLoggedInRoleEntity] = useState<string>("");
    const [extraUserRoleIdsLocal, setExtraUserRoleIdsLocal] = useState<number[]>([]);
    const [onlyExtraRoleEntity, setOnlyExtraRoleEntity] = useState<string>("");
    const [allExtraRoleEntityLoaded, setAllExtraRoleEntityLoaded] = useState(false);
    const [selectedRoleEntity, setSelectedRoleEntity] = useState<string>("");
    const [userEntities, setUserEntities] = useState<{entity: string, value: number | string}[]>([]);
    const [showPDF, setShowPDF] = useState<{ isShow: boolean, row: BaaDetailDTO | null }>({ isShow: false, row: null });
    const { baaList } = useCreateBusinessAgreementSlice();
    const { searchUsers, userRoles, agencyInfoListUserSlice, billerListUserSlice, manufacturerInfoListUserSlice, practiceByUserRoleUserSlice } = useUserReducer();
    const { baaDocument } = useCreateBusinessAgreementSlice();
    const { document } = usePracticeSlice();
    const [searchCriteria, setSearchCriteria] = useState<SearchBaaRequest>({
        userRoleIds: [],
        entityIds: [],
        firstName: "",
        lastName: "",
        status: [],
        currentPage: 1,
        itemsPerPage: ITEMS_PER_PAGE,
        email: "" //CS-1696
      });

    const canSignBaa = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessBaaActions", BAA_ACTIONS.SIGN)(state));
    const canPreviewBaa = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessBaaActions", BAA_ACTIONS.PREVIEW)(state));
    const [fetchInitDataCompleted, setFetchInitDataCompleted] = useState<boolean>(false);
    const [activeButtonId, setActiveButtonId] = useState<string | null>(null); // CS-1555
    // CS-1685 - Start
    const canExportBaa = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.BAA)(state));
    const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
    const initialSearchDoneRef = useRef(false);
    const processedNavigationStateRef = useRef<typeof location.state | null>(null);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElMenu(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorElMenu(null);
    };

    const handleExport = (exportType: string) => {

        if (searchCriteria) {
            let dataToSearch = {
                ...searchCriteria,
                currentPage: searchCriteria.currentPage,
                downloadState: true
            }
            ExportToExcel(
                dataToSearch,
                columns,
                dispatch,
                EXPORTABLE_ITEMS.BAA,
            )
        }
        handleMenuClose();
    };
    // CS-1685 - End
    //Mount
    useEffect(() => {
        fetchInitialLists();
    }, []);

    const fetchInitialLists = async () => {
        dispatch(showHideLoadingDialog(true));
        if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY) {
            setLoggedInRoleEntity(AGENCY_ENTITY);
            await dispatch(fetchAgenciesListUserSlice());
        } else if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER) {
            setLoggedInRoleEntity(MANUFACTURER_ENTITY);
            await dispatch(fetchManufacturersListUserSlice());
        } else if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE) {
            setLoggedInRoleEntity(PRACTICE_ENTITY);
            await dispatch(getPracticeListByUserRoleAgreementSlice(true));
            await dispatch(getPracticesByUserRoleUserSlice(true));
        } else if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.BILLER) {
            setLoggedInRoleEntity(BILLER_ENTITY);
            await dispatch(fetchActiveBillersUserSlice());
        } else {
            const responsePractices = await dispatch(getPracticeListByUserRoleAgreementSlice(true));
            const responseAgencies = await dispatch(fetchAgenciesListUserSlice());
            const responseBillers = await dispatch(fetchActiveBillersUserSlice());
            const responseManufacturers = await dispatch(fetchManufacturersListUserSlice());
            await dispatch(getPracticesByUserRoleUserSlice(true));
            if (responsePractices && responseAgencies && responseBillers && responseManufacturers) {
                setFetchInitDataCompleted(true);
            }
        }
        const responseAllUserRoles = await dispatch(fetchAllUserRoles());
        if (responseAllUserRoles) {
            setFetchInitDataCompleted(true);
        }
        dispatch(showHideLoadingDialog(false));
    }

    const sortUserTypeList = (options: Option[]) => {
        return options.sort((a, b) => {
            const indexA = orderedUserList.indexOf(a.label);
            const indexB = orderedUserList.indexOf(b.label);
            return indexA - indexB;
        });
    };

    const practiceOptions = useMemo(() => {
        let options: Option[] = [];
        if (practiceByUserRoleUserSlice && practiceByUserRoleUserSlice.length > 0) {
            const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRoleUserSlice, 'practiceName');
            sortedPracticeInfo?.map(practice => {
                options.push({ value: practice.practiceId, label: practice.practiceName });
            });
        }
        return options;
    }, [practiceByUserRoleUserSlice]);

    const statusOptions = useMemo(() => {
        //CS-1696 - Start
        let options: Option[] = [
            { value: BAA_STATUS.SIGNED, label: BAA_STATUS.SIGNED },
            { value: BAA_STATUS.INACTIVE, label: BAA_STATUS.INACTIVE }
        ];
        setValue('status', [BAA_STATUS.SIGNED])
        //CS-1696 - End
        return options;
    }, []);

    const baaRows = useMemo(() => {
        const rows: BaaDetailDTO[] = [];
        baaList?.content?.map(row => {
            const status = row.agreementStatus.replace("_", " ");
            rows.push({ ...row, agreementStatus: status })
        })
        return rows;
    }, [baaList]);

    
  const agencyList = useMemo(() => {
    let options: Option[] = [];
    if (agencyInfoListUserSlice && agencyInfoListUserSlice.length > 0) {
      const isAgencyEntityUser =
        userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY ||
        userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE;
      const userAgencyIds = (userInfo.entityIds || []).map(Number);

      // Agency/Account Rep users must see their own agencies.
      const filteredAgencyList = agencyInfoListUserSlice.filter((agency: AgencyInfo) => {
        const agencyId = Number(agency.agencyId);
        if (isAgencyEntityUser && userAgencyIds.includes(agencyId)) {
          return true;
        }
        return true;
      });

      const sortedInfo: AgencyInfo[] = sortNames(filteredAgencyList, 'agencyName');
      sortedInfo?.map(option => {
        options.push({ label: option.agencyName, value: Number(option.agencyId) });
      });
    }
    return options;
  }, [agencyInfoListUserSlice, userInfo.entityName, userInfo.entityIds]);

  const billerUsersList = useMemo(() => {
    let options: Option[] = [];
    if (billerListUserSlice && billerListUserSlice.length > 0) {
      const sortedInfo: BillerInfo[] = sortNames(billerListUserSlice, 'billerName');
      sortedInfo?.map(option => {
        options.push({ label: option.billerName, value: option.billerId });
      });
    }
    return options;
  }, [billerListUserSlice]);

  const manufacturersList = useMemo(() => {
    let options: Option[] = [];
    if (manufacturerInfoListUserSlice && manufacturerInfoListUserSlice.length > 0) {
      const sortedInfo: ManufacturerInfo[] = sortNames(manufacturerInfoListUserSlice, 'manufacturerName');
      sortedInfo?.map(option => {
        options.push({ label: option.manufacturerName, value: option.manufacturerId });
      });
    }
    return options;
  }, [manufacturerInfoListUserSlice]);

    const legacyItems = [
        { icon: <DoDisturbAltOutlined color="warning" />, text: "Inactivate" }
    ];

    const getLoggedInRoleEntity = (): string => {
        if (loggedInRoleEntity) {
            return loggedInRoleEntity;
        }
        if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY || userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE) {
            return AGENCY_ENTITY;
        }
        if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER) {
            return MANUFACTURER_ENTITY;
        }
        if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE) {
            return PRACTICE_ENTITY;
        }
        if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.BILLER) {
            return BILLER_ENTITY;
        }
        return "";
    };

    const isEntityDataReadyForInitialSearch = (): boolean => {
        const entity = getLoggedInRoleEntity();
        if (!entity) {
            return true;
        }
        if (entity === PRACTICE_ENTITY) {
            return !!practiceByUserRoleUserSlice;
        }
        if (entity === AGENCY_ENTITY) {
            return !!agencyInfoListUserSlice;
        }
        if (entity === MANUFACTURER_ENTITY) {
            return !!manufacturerInfoListUserSlice;
        }
        if (entity === BILLER_ENTITY) {
            return !!billerListUserSlice;
        }
        return true;
    };

    const getDefaultEntityIdsFromUserInfo = (activeEntity: string): number[] => {
        if (!userInfo.entityIds?.length) {
            return [];
        }
        if (activeEntity === PRACTICE_ENTITY && userInfo.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE) {
            return userInfo.entityIds.map(Number);
        }
        if (activeEntity === AGENCY_ENTITY && (userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY || userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE)) {
            return userInfo.entityIds.map(Number);
        }
        if (activeEntity === MANUFACTURER_ENTITY && userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER) {
            return userInfo.entityIds.map(Number);
        }
        if (activeEntity === BILLER_ENTITY && userInfo.entityName === USER_ROLE_ENTITY_NAMES.BILLER) {
            return userInfo.entityIds.map(Number);
        }
        return [];
    };

    const getActiveEntity = (): string => selectedRoleEntity || getLoggedInRoleEntity();

    const getAvailableEntityIds = (entity: string): number[] => {
        if (entity === PRACTICE_ENTITY) {
            return (practiceByUserRoleUserSlice || [])
                .map((practice) => Number(practice.practiceId))
                .filter(Boolean);
        }
        if (entity === AGENCY_ENTITY) {
            return agencyList.map((option) => Number(option.value)).filter(Boolean);
        }
        if (entity === MANUFACTURER_ENTITY) {
            return (manufacturerInfoListUserSlice || [])
                .map((manufacturer) => Number(manufacturer.manufacturerId))
                .filter(Boolean);
        }
        if (entity === BILLER_ENTITY) {
            return (billerListUserSlice || [])
                .map((biller) => Number(biller.billerId))
                .filter(Boolean);
        }
        return [];
    };

    /** When an entity-scoped user type has no selectable entities, send [0] so the API returns no rows instead of all entities. */
    const resolveEntityIdsForApi = (entityIds: number[], activeEntity: string): number[] => {
        if (entityIds.length > 0) {
            return entityIds;
        }
        if (activeEntity) {
            return [0];
        }
        return [];
    };

    const getEntityIdsForSearch = (): number[] => {
        const activeEntity = getActiveEntity();
        if (activeEntity === PRACTICE_ENTITY) {
            const formIds = (getValues("practiceIds") || []).map(Number).filter(Boolean);
            if (formIds.length > 0) {
                return formIds;
            }
            const userEntityIds = getDefaultEntityIdsFromUserInfo(activeEntity);
            if (userEntityIds.length > 0) {
                return userEntityIds;
            }
            // When no practice is selected,
            // scope the API to all practices available to this user; [0] if none.
            return resolveEntityIdsForApi(getAvailableEntityIds(PRACTICE_ENTITY), PRACTICE_ENTITY);
        }
        if (activeEntity === AGENCY_ENTITY) {
            const formIds = (getValues("agencyIds") || []).map(Number).filter(Boolean);
            if (formIds.length > 0) {
                return formIds;
            }
            const userEntityIds = getDefaultEntityIdsFromUserInfo(activeEntity);
            if (userEntityIds.length > 0) {
                return userEntityIds;
            }
            return resolveEntityIdsForApi(getAvailableEntityIds(AGENCY_ENTITY), AGENCY_ENTITY);
        }
        if (activeEntity === MANUFACTURER_ENTITY) {
            const formIds = (getValues("manufactureIds") || []).map(Number).filter(Boolean);
            if (formIds.length > 0) {
                return formIds;
            }
            const userEntityIds = getDefaultEntityIdsFromUserInfo(activeEntity);
            if (userEntityIds.length > 0) {
                return userEntityIds;
            }
            return resolveEntityIdsForApi(getAvailableEntityIds(MANUFACTURER_ENTITY), MANUFACTURER_ENTITY);
        }
        if (activeEntity === BILLER_ENTITY) {
            const formIds = (getValues("billerIds") || []).map(Number).filter(Boolean);
            if (formIds.length > 0) {
                return formIds;
            }
            const userEntityIds = getDefaultEntityIdsFromUserInfo(activeEntity);
            if (userEntityIds.length > 0) {
                return userEntityIds;
            }
            return resolveEntityIdsForApi(getAvailableEntityIds(BILLER_ENTITY), BILLER_ENTITY);
        }
        return [];
    };

    const setEntityIdsToForm = (entity: string, entityIds: number[]) => {
        if (entity === PRACTICE_ENTITY) {
            setValue("practiceIds", entityIds);
        } else if (entity === MANUFACTURER_ENTITY) {
            setValue("manufactureIds", entityIds);
        } else if (entity === AGENCY_ENTITY) {
            setValue("agencyIds", entityIds);
        } else if (entity === BILLER_ENTITY) {
            setValue("billerIds", entityIds);
        }
    };

    /** Auto-scoped / sentinel IDs belong in the API only — keep the multi-select empty for the user. */
    const shouldPopulateEntityIdsInForm = (entity: string, entityIds: number[]): boolean => {
        if (!entityIds?.length) {
            return false;
        }
        // No available entities — API uses [0]; do not show that in the field
        if (entityIds.length === 1 && entityIds[0] === 0) {
            return false;
        }
        // Single explicit/default selection is fine to show
        if (entityIds.length === 1) {
            return true;
        }
        const availableIds = getAvailableEntityIds(entity);
        // Options not loaded yet — do not dump a bulk auto-scoped list into the UI
        if (availableIds.length === 0) {
            return false;
        }
        // Full available set was applied for scoping (not an explicit user selection)
        if (entityIds.length === availableIds.length) {
            const availableSet = new Set(availableIds);
            if (entityIds.every((id) => availableSet.has(id))) {
                return false;
            }
        }
        return true;
    };

    const shouldDefaultEntityField = (entity: string): boolean => {
        const loggedInEntity = getLoggedInRoleEntity();
        return (
            (loggedInEntity === entity && selectedRoleEntity === "") ||
            (selectedRoleEntity === entity && loggedInEntity === "") ||
            (selectedRoleEntity !== "" && loggedInEntity !== "" && loggedInEntity === entity) ||
            (selectedRoleEntity !== "" && loggedInEntity !== "" && selectedRoleEntity !== loggedInEntity && selectedRoleEntity === entity)
        );
    };

    const applyDefaultEntityFieldValue = (entity: string, defaultIds: number[]) => {
        if (!shouldDefaultEntityField(entity) || defaultIds.length === 0) {
            return;
        }
        setEntityIdsToForm(entity, defaultIds);
    };

    useEffect(() => {
        if (!manufacturerInfoListUserSlice?.length) {
            return;
        }
        let defaultIds = getDefaultEntityIdsFromUserInfo(MANUFACTURER_ENTITY);
        if (defaultIds.length === 0 && manufacturerInfoListUserSlice.length === 1) {
            defaultIds = [Number(manufacturerInfoListUserSlice[0]?.manufacturerId)];
        }
        applyDefaultEntityFieldValue(MANUFACTURER_ENTITY, defaultIds);
    }, [manufacturerInfoListUserSlice, loggedInRoleEntity, selectedRoleEntity, userInfo.entityIds]);

    useEffect(() => {
        if (!agencyList.length) {
            return;
        }
        let defaultIds = getDefaultEntityIdsFromUserInfo(AGENCY_ENTITY);
        if (defaultIds.length === 0 && agencyList.length === 1) {
            defaultIds = [Number(agencyList[0]?.value)];
        }
        // Prefer IDs that exist in the Agency dropdown options
        if (defaultIds.length > 0) {
            const optionIds = agencyList.map((option) => Number(option.value));
            const matchedIds = defaultIds.filter((id) => optionIds.includes(id));
            if (matchedIds.length > 0) {
                defaultIds = matchedIds;
            } else if (agencyList.length === 1) {
                defaultIds = [Number(agencyList[0]?.value)];
            }
        }
        applyDefaultEntityFieldValue(AGENCY_ENTITY, defaultIds);
    }, [agencyList, loggedInRoleEntity, selectedRoleEntity, userInfo.entityIds]);

    useEffect(() => {
        if (!billerListUserSlice?.length) {
            return;
        }
        let defaultIds = getDefaultEntityIdsFromUserInfo(BILLER_ENTITY);
        if (defaultIds.length === 0 && billerListUserSlice.length === 1) {
            defaultIds = [Number(billerListUserSlice[0]?.billerId)];
        }
        applyDefaultEntityFieldValue(BILLER_ENTITY, defaultIds);
    }, [billerListUserSlice, loggedInRoleEntity, selectedRoleEntity, userInfo.entityIds]);

    useEffect(() => {
        if (!practiceByUserRoleUserSlice?.length) {
            return;
        }
        let defaultIds = getDefaultEntityIdsFromUserInfo(PRACTICE_ENTITY);
        if (defaultIds.length === 0 && practiceByUserRoleUserSlice.length === 1) {
            defaultIds = [Number(practiceByUserRoleUserSlice[0]?.practiceId)];
        }
        applyDefaultEntityFieldValue(PRACTICE_ENTITY, defaultIds);
    }, [practiceByUserRoleUserSlice, loggedInRoleEntity, selectedRoleEntity, userInfo.entityIds]);

    useEffect(() => {
        if (onlyExtraRoleEntity && onlyExtraRoleEntity !== NONE_ENTITY) {
            fetchExtraRoleEntityLists();
        } else if (onlyExtraRoleEntity === NONE_ENTITY) {
            setAllExtraRoleEntityLoaded(true);
        }
    }, [onlyExtraRoleEntity]);

    const fetchExtraRoleEntityLists = async () => {
        if (onlyExtraRoleEntity === AGENCY_ENTITY) {
            await dispatch(fetchAgenciesListUserSlice());
        } else if (onlyExtraRoleEntity === MANUFACTURER_ENTITY) {
            await dispatch(fetchManufacturersListUserSlice());
        } else if (onlyExtraRoleEntity === PRACTICE_ENTITY) {
            await dispatch(getPracticesByUserRoleUserSlice(true));
        } else if (onlyExtraRoleEntity === BILLER_ENTITY) {
            await dispatch(fetchActiveBillersUserSlice());
        }
        setAllExtraRoleEntityLoaded(true);
    };

    const loadInitialBaaList = (pNumber: number = 1) => {
        const defaultUserRoleId = userTypesList && userTypesList.length === 1 ? userTypesList[0].value : null;
        const userRoleIds = buildUserRoleIdsForSearch(
            defaultUserRoleId,
            getValues("extraRoleIds") || extraUserRoleIdsLocal
        );
        const entityIds = getEntityIdsForSearch();

        const data : SearchBaaRequest = {
            userRoleIds,
            entityIds,
            firstName: "",
            lastName: "",
            status: [BAA_STATUS.SIGNED],
            email: "", //CS-1696
          currentPage: pNumber,
          itemsPerPage: ITEMS_PER_PAGE,
        };
        setSearchCriteria(data);
        setValuesToCriteriaFields(data, defaultUserRoleId);

        searchBaaList(data);
      };

    const setValuesToCriteriaFields = (
        searchCriteria: SearchBaaRequest,
        userRoleIdForm: number | string | null = null
    ) => {
        setValue("firstName", searchCriteria.firstName);
        setValue("lastName", searchCriteria.lastName);
        setValue("status", searchCriteria.status);

        if (userRoleIdForm !== null && userRoleIdForm !== "") {
            setValue("userRoleId", userRoleIdForm);
        } else {
            setValue("userRoleId", undefined);
        }

        const user = userRoleIdForm ?? getValues("userRoleId");
        const selectedUser = userEntities?.find(entity => entity.value === user);
        const activeEntity = selectedUser?.entity || getLoggedInRoleEntity();

        if (searchCriteria.entityIds?.length > 0 && activeEntity && shouldPopulateEntityIdsInForm(activeEntity, searchCriteria.entityIds)) {
            setEntityIdsToForm(activeEntity, searchCriteria.entityIds);
        }
    }

      const searchBaaList = async (searchCriteria: SearchBaaRequest) => {
        const normalizedCriteria = baaListSort.normalize(searchCriteria);
        setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.BAA_MANAGEMENT + "SearchCriteria" + userInfo.userId, normalizedCriteria);
        
        let dataToSearch = {
          ...normalizedCriteria
        }
        const response = await dispatch(searchBaaDetails(dataToSearch));
      }
    

    const onSubmit = async (data: SearchBaaFields) => {
        const userRoleIds = buildUserRoleIdsForSearch(
            getValues("userRoleId"),
            getValues("extraRoleIds") || []
        );

        const activeEntity = getActiveEntity();
        let selectedEntityIds: number[] = [];

        if (activeEntity === PRACTICE_ENTITY) {
            selectedEntityIds = (data.practiceIds || []).map(Number).filter(Boolean);
        } else if (activeEntity === MANUFACTURER_ENTITY) {
            selectedEntityIds = (data.manufactureIds || []).map(Number).filter(Boolean);
        } else if (activeEntity === AGENCY_ENTITY) {
            selectedEntityIds = (data.agencyIds || []).map(Number).filter(Boolean);
        } else if (activeEntity === BILLER_ENTITY) {
            selectedEntityIds = (data.billerIds || []).map(Number).filter(Boolean);
        }

        // No entity selected in the filter — use all entities available to this user.
        // If the selected user type has no entity options, send [0].
        if (selectedEntityIds.length === 0 && activeEntity) {
            selectedEntityIds = resolveEntityIdsForApi(getAvailableEntityIds(activeEntity), activeEntity);
        }

        const searchRequest: SearchBaaRequest = {
            userRoleIds,
            entityIds: selectedEntityIds,
            firstName: data.firstName,
            lastName: data.lastName,
            status: data.status,
            currentPage: 1,
            itemsPerPage: ITEMS_PER_PAGE,
            email: data.email || "", //CS-1696
            ...baaListSort.pick(searchCriteria),
        };
        setSearchCriteria(searchRequest);
        navigate("/baa_detail", {
            state: {
                baaListSearchCriteria: searchRequest,
                baaUserRoleId: getValues("userRoleId") ?? null,
            },
        });
    }

    const handlePagination = (pageNumber: number) => {
        const data = {
          ...searchCriteria,
          currentPage: pageNumber + 1
        };
        setSearchCriteria(data);
    
        navigate("/baa_detail", {
            state: {
                baaListSearchCriteria: data,
                baaUserRoleId: getValues("userRoleId") ?? null,
            },
        });
      }

    const handleTableSort = (columnId: string, order: "asc" | "desc") => {
        const data = baaListSort.withSort({
            ...searchCriteria,
            itemsPerPage: searchCriteria.itemsPerPage || ITEMS_PER_PAGE,
        }, columnId, order);
        if (!data) {
            return;
        }
        setSearchCriteria(data);
        navigate("/baa_detail", {
            state: {
                baaListSearchCriteria: data,
                baaUserRoleId: getValues("userRoleId") ?? null,
            },
        });
    }

  const handlePreview = async (row: BaaDetailDTO) => {
    if(row.agreementDocumentId) {
        const actionResult = await dispatch( fetchDocumentById((row.agreementDocumentId).toString()) );
        const documentData = actionResult.payload
        if (documentData && documentData.content) {
            setShowPDF({isShow: true, row:row});
          }
    }
  }

  // CS-1555 - Start
    useEffect(() => {
        const saved = sessionStorage.getItem("activeActionButtonId");
        if (saved) setActiveButtonId(saved);
    }, []);

    const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
        const currentPageKey = "baaList";
        saveSelectionForPage(currentPageKey, buttonId);
        setActiveButtonId(buttonId);
        sessionStorage.setItem("activeActionButtonId", buttonId);
        originalClick();
    };

    const handleRowNavigateToPdf = (row: Row) => {
        const r = row as BaaDetailDTO;
        if (!canPreviewBaa) return;
        if (!r?.agreementDocumentId) return;
        handleActionButtonClick(`baaListActionButton-${r.baaDetailId}`, () => handlePreview(r));
    };
    // CS-1555 - End
    const totalRecords = baaList?.totalElements ?? 0;
    const actionComponent = (row: BaaDetailDTO): JSX.Element => {
        return (
            <>
                <CustomIconButton
                    color="info"
                    size="small"
                    variant="outlined"
                    // CS-1555 - Start
                    onClick={() =>
                        handleActionButtonClick(
                            `baaListActionButton-${row.baaDetailId}`,
                            () => navigate("/baa_agreement", { state: { baaDetailId: row.baaDetailId, searchBaaDetails: searchCriteria } })
                        )
                    }
                    // CS-1555 - End
                    tooltipMessage="Sign"
                    tooltipPlacement="top"
                    style={{ display: `${hideElement(!canSignBaa || row.agreementDocumentId !== null)}` }}
                >
                    <ModeOutlined fontSize="small" />
                </CustomIconButton>
                <CustomIconButton
                    color="info"
                    size="small"
                    variant="outlined"
                    // CS-1555 - Start
                    onClick={() =>
                        handleActionButtonClick(
                            `baaListActionButton-${row.baaDetailId}`,
                            () => handlePreview(row)
                        )
                    }
                    // CS-1555 - End
                    tooltipMessage="PDF"
                    tooltipPlacement="top"
                    style={{ display: `${hideElement(!canPreviewBaa)}` }}
                >
                    <PictureAsPdfOutlined fontSize="small" />
                </CustomIconButton>
            </>
        );
    }

    const userTypesList = useMemo(() => {
        let allUserRoles: Option[] = [];
        let entities: { entity: string, value: number | string }[] = [];
        let loggedInUserEntityRelatedRoles: number[] = [];
        let extraUserRoleIds: number[] = [];
        let allEntityRelatedRoles: string[] = [];
        let entityRelatedLoggedInUser = false;

        if (userRoles?.length && managingUserRoles?.length) {
            const availableUserRoles = userRoles.filter((role: UserRole) =>
                managingUserRoles.includes(role.roleName)
            );

            const entityGroupedRoles: Record<string, number[]> = {};

            availableUserRoles.forEach((role: UserRole) => {
                allUserRoles.push({ label: role.roleDisplayValue, value: role.userRoleId });

                const normalizedEntity = getNormalizedEntity(role.entityName);
                if (normalizedEntity) {
                    if (!entityGroupedRoles[normalizedEntity]) {
                        entityGroupedRoles[normalizedEntity] = [];
                    }
                    entityGroupedRoles[normalizedEntity].push(parseInt(role.userRoleId));

                    if (!allEntityRelatedRoles.includes(normalizedEntity)) {
                        allEntityRelatedRoles.push(normalizedEntity);
                    }

                    entities.push({
                        entity: toRoleEntityDisplayName(normalizedEntity),
                        value: role.userRoleId,
                    });

                    if (loggedInUserRole?.startsWith(`ROLE_${normalizedEntity}`)) {
                        entityRelatedLoggedInUser = true;
                        loggedInUserEntityRelatedRoles.push(parseInt(role.userRoleId));
                    }
                }
            });

            Object.entries(entityGroupedRoles).forEach(([entity, roleIds]) => {
                if (roleIds.length > 1) {
                    const combinedValue = roleIds.join("-");
                    allUserRoles.push({
                        label: `All ${entity.charAt(0) + entity.slice(1).toLowerCase()} Users`,
                        value: combinedValue,
                    });
                    entities.push({
                        entity: toRoleEntityDisplayName(entity),
                        value: combinedValue,
                    });
                }
            });

            if (entityRelatedLoggedInUser) {
                allUserRoles.forEach((option) => {
                    if (
                        Number(option.value) &&
                        !loggedInUserEntityRelatedRoles.includes(Number(option.value))
                    ) {
                        extraUserRoleIds.push(Number(option.value));
                    }
                });

                if (extraUserRoleIds.length > 1) {
                    setValue("extraRoleIds", extraUserRoleIds);
                    setExtraUserRoleIdsLocal(extraUserRoleIds);
                }
            }

            if (loggedInUserEntityRelatedRoles.length === 0 && allEntityRelatedRoles.length === 1) {
                setOnlyExtraRoleEntity(toRoleEntityDisplayName(allEntityRelatedRoles[0]));
            } else {
                setOnlyExtraRoleEntity(NONE_ENTITY);
            }

            allUserRoles = sortUserTypeList(allUserRoles);

            if (allUserRoles.length === 1) {
                setValue("userRoleId", allUserRoles[0]?.value);
            }
        }

        setUserEntities(entities);
        return allUserRoles;
    }, [userRoles, managingUserRoles, loggedInUserRole]);

    const buildUserRoleIdsForSearch = (
        userRoleIdForm: number | string | null | undefined,
        extraRoleIds: number[] = []
    ): number[] => {
        let userRoleIds: number[] = [];

        if (!userRoleIdForm) {
            userTypesList.forEach((option: Option) => {
                if (!String(option.value).includes("-")) {
                    userRoleIds.push(Number(option.value));
                }
            });

            if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN) {
                if (extraRoleIds.length > 0) {
                    userRoleIds = Array.from(new Set([...userRoleIds, ...extraRoleIds]));
                }
            } else if (userRoleIds.length > 0 && extraRoleIds.length > 0) {
                userRoleIds = userRoleIds.filter((roleId) => !extraRoleIds.includes(roleId));
            }
        } else if (String(userRoleIdForm).includes("-")) {
            String(userRoleIdForm).split("-").forEach((val: string) => {
                userRoleIds.push(Number(val));
            });
        } else {
            userRoleIds = [Number(userRoleIdForm)];
        }

        return userRoleIds;
    };

    const handleUserTypeChange = () => {
        if (!loggedInRoleEntity) {
            setValue("practiceIds", []);
            setValue("manufactureIds", []);
            setValue("agencyIds", []);
            setValue("billerIds", []);
        }
    };

    useEffect(() => {
        const userRoleIdLocal = getValues("userRoleId");
        if (!userRoleIdLocal) {
            if (onlyExtraRoleEntity && onlyExtraRoleEntity !== NONE_ENTITY) {
                setSelectedRoleEntity(onlyExtraRoleEntity);
            } else {
                setSelectedRoleEntity("");
            }
            setValue("extraRoleIds", extraUserRoleIdsLocal);
            return;
        }

        let oneOfRoleId = 0;
        let extraUserRoleIds: number[] = [];
        if (String(userRoleIdLocal).includes("-")) {
            String(userRoleIdLocal).split("-").forEach((val: string) => {
                oneOfRoleId = Number(val);
                if (extraUserRoleIdsLocal.includes(oneOfRoleId)) {
                    extraUserRoleIds.push(oneOfRoleId);
                }
            });
        } else {
            oneOfRoleId = Number(userRoleIdLocal);
            if (extraUserRoleIdsLocal.includes(oneOfRoleId)) {
                extraUserRoleIds.push(oneOfRoleId);
            }
        }

        setValue("extraRoleIds", extraUserRoleIds);

        let newSelectedRoleEntity = "";
        const entityName = userRoles?.find((option: UserRole) => Number(option.userRoleId) === oneOfRoleId)?.entityName;
        const normalizedEntity = getNormalizedEntity(entityName);
        if (normalizedEntity === USER_ROLE_ENTITY_NAMES.AGENCY) {
            newSelectedRoleEntity = AGENCY_ENTITY;
        } else if (normalizedEntity === USER_ROLE_ENTITY_NAMES.MANUFACTURER) {
            newSelectedRoleEntity = MANUFACTURER_ENTITY;
        } else if (normalizedEntity === USER_ROLE_ENTITY_NAMES.PRACTICE) {
            newSelectedRoleEntity = PRACTICE_ENTITY;
        } else if (normalizedEntity === USER_ROLE_ENTITY_NAMES.BILLER) {
            newSelectedRoleEntity = BILLER_ENTITY;
        }

        if (newSelectedRoleEntity !== selectedRoleEntity) {
            if (newSelectedRoleEntity === AGENCY_ENTITY) {
                dispatch(fetchAgenciesListUserSlice());
            } else if (newSelectedRoleEntity === MANUFACTURER_ENTITY) {
                dispatch(fetchManufacturersListUserSlice());
            } else if (newSelectedRoleEntity === PRACTICE_ENTITY) {
                dispatch(getPracticesByUserRoleUserSlice(true));
            } else if (newSelectedRoleEntity === BILLER_ENTITY) {
                dispatch(fetchActiveBillersUserSlice());
            }
        }

        setSelectedRoleEntity(newSelectedRoleEntity);
    }, [watch("userRoleId")]);

    useEffect(() => {
        if (!userTypesList || userTypesList.length === 0 || !onlyExtraRoleEntity || (onlyExtraRoleEntity !== NONE_ENTITY && !allExtraRoleEntityLoaded)) {
            return;
        }

        let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.BAA_MANAGEMENT + "SearchCriteria" + userInfo.userId);
        const hasNavigationSearch = (location.state && location.state.baaListSearchCriteria)
            || (location.state && location.state.withSearchHistory && criteria);

        if (hasNavigationSearch) {
            if (location.state && location.state.withSearchHistory) {
                navigate("/baa_Detail", { state: { baaListSearchCriteria: criteria, baaUserRoleId: null } });
                return;
            }

            if (processedNavigationStateRef.current === location.state) {
                return;
            }
            processedNavigationStateRef.current = location.state;

            let searchCriteria: SearchBaaRequest = {
                userRoleIds: [],
                entityIds: [],
                firstName: "",
                lastName: "",
                status: [],
                currentPage: 0,
                itemsPerPage: 0,
                email: ""
            };
            if (location.state && location.state.baaListSearchCriteria) {
                searchCriteria = baaListSort.normalize(location.state.baaListSearchCriteria);
            } else {
                searchCriteria = baaListSort.normalize(criteria ? criteria : searchCriteria);
            }
            setSearchCriteria(searchCriteria);
            setValuesToCriteriaFields(
                searchCriteria,
                location.state?.baaUserRoleId ?? null
            );
            searchBaaList(searchCriteria);
            return;
        }

        if (!initialSearchDoneRef.current) {
            if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
                if (!isEntityDataReadyForInitialSearch()) {
                    return;
                }
                initialSearchDoneRef.current = true;
                loadInitialBaaList(1);
            }
        }
    }, [location.state, userTypesList, onlyExtraRoleEntity, allExtraRoleEntityLoaded, practiceByUserRoleUserSlice, agencyInfoListUserSlice, manufacturerInfoListUserSlice, billerListUserSlice]);

    // Unmount
    useEffect(() => () => {
        dispatch(updateElementInBusinessAgreement({
            elementName: "baaList",
            value: undefined
        }))
    }, []);

    function HandlePracticeBAAs(): void {
        navigate("/practice_list", {state: {fromBaa : true}});
    }

    function handleUserBAAs(): void {
        navigate("/user_list", {state: {fromBaa : true}});
    }

    // -- Design tokens
    const baaBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
    const baaPanelBg    = isDark ? "#141720" : "rgba(255,255,255,0.82)";
    const baaHoverBg    = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

    const exportBtnSx = {
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.86rem",
        borderRadius: "10px",
        py: 1.05,
        px: 1.6,
        color: "#4ade80",
        borderColor: "rgba(74,222,128,0.55)",
        backgroundColor: "transparent",
        whiteSpace: "nowrap",
        "& .MuiSvgIcon-root": {
            color: "#4ade80",
        },
        "&:hover": {
            color: "#fff",
            borderColor: "#22c55e",
            backgroundColor: "rgba(34,197,94,0.18)",
            boxShadow: "0 6px 22px rgba(34,197,94,0.24)",
            "& .MuiSvgIcon-root": {
                color: "#fff",
            },
        },
    };

    const baaCardSx = {
        borderRadius: "14px",
        border: `1px solid ${baaBorderColor}`,
        backgroundColor: baaPanelBg,
        backgroundImage: "none",
        backdropFilter: "blur(8px)",
        boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
    };

    const filterPanelSx = {
        ...baaCardSx,
        p: { xs: "14px !important", md: "18px !important" },
        "& .MuiAutocomplete-root, & .MuiFormControl-root": {
            width: "100%",
        },
        "& .MuiTextField-root": {
            height: "42px",
            minHeight: "42px !important",
        },
        "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
            minHeight: "42px !important",
            height: "42px !important",
            borderRadius: "10px !important",
            backgroundColor: `${modalTokens.inputBg} !important`,
            color: modalTokens.textPrimary,
            fontSize: "0.88rem",
            boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
            alignItems: "center",
            boxSizing: "border-box",
        },
        "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
            padding: "0 38px 0 12px !important",
        },
        "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
            paddingTop: "8px !important",
            paddingBottom: "8px !important",
            height: "24px !important",
            lineHeight: "24px !important",
            boxSizing: "border-box",
        },
        "& .MuiOutlinedInput-input": {
            paddingLeft: "12px !important",
            paddingRight: "12px !important",
        },
        "& .MuiAutocomplete-endAdornment": {
            top: "50% !important",
            transform: "translateY(-50%)",
            right: "8px !important",
        },
        "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
            borderColor: `${modalTokens.inputBorder} !important`,
        },
        "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
            borderColor: `${modalTokens.accent} !important`,
            borderWidth: "1px !important",
        },
        "& .MuiOutlinedInput-root.Mui-focused": {
            boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
        },
        "& .MuiInputBase-input": {
            color: modalTokens.textPrimary,
            fontSize: "0.88rem",
        },
        "& input::selection": {
            color: modalTokens.textPrimary,
            backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
        },
        "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
            backgroundColor: "#376c9f !important",
            "& fieldset": { borderColor: "#7fb1df !important" },
        },
        "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitTextFillColor: "#ffffff",
            caretColor: "#ffffff",
            WebkitBoxShadow: "0 0 0 100px #376c9f inset",
            borderRadius: "10px",
            transition: "background-color 5000s ease-in-out 0s",
        },
        "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
        "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
        "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
        "& .MuiChip-root": {
            borderRadius: "999px",
            backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
            color: modalTokens.accent,
            border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
            fontWeight: 600,
        },
    };

    const primaryActionBtnSx = {
        ...primaryBtnSx,
        color: "#fff",
        "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
        "& .MuiButton-startIcon": { color: "#fff" },
    };

    const practiceBaaBtnSx = {
        ...primaryActionBtnSx,
        backgroundColor: "#6fc36a",
        borderColor: "#6fc36a",
        color: "#ffffff",
        "& .MuiTypography-root": { color: "#ffffff", fontWeight: 700 },
        "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
        "&:hover": {
            backgroundColor: "#5cb85c",
            borderColor: "#5cb85c",
            color: "#ffffff",
            "& .MuiTypography-root": { color: "#ffffff" },
            "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
        },
    };

    const userBaaBtnSx = {
        ...primaryActionBtnSx,
        backgroundColor: "#f5a623",
        borderColor: "#f5a623",
        color: "#ffffff",
        "& .MuiTypography-root": { color: "#ffffff", fontWeight: 700 },
        "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
        "&:hover": {
            backgroundColor: "#e89a1f",
            borderColor: "#e89a1f",
            color: "#ffffff",
            boxShadow: "0 6px 22px rgba(245,166,35,0.28)",
            "& .MuiTypography-root": { color: "#ffffff" },
            "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
        },
    };

    const outlineBtnSx = {
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.86rem",
        borderRadius: "10px",
        py: 1.05,
        px: 1.6,
        color: "#00B8D9",
        borderColor: "rgba(0,184,217,0.55)",
        backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
        whiteSpace: "nowrap",
        "&:hover": {
            color: "#fff",
            borderColor: "#00B8D9",
            backgroundColor: "#00B8D9",
            boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
        },
    };

    const exportMenuItemBtnSx = {
        justifyContent: "flex-start",
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.86rem",
        borderRadius: "8px",
        py: 1,
        px: 1.4,
        color: "#2dd4bf",
        borderColor: "rgba(45,212,191,0.35)",
        backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
        boxShadow: "none",
        "& .MuiButton-startIcon": {
            mr: 1,
            "& svg": { fontSize: "18px" },
        },
        "&:hover": {
            color: "#fff",
            borderColor: "#2f9b8f",
            backgroundColor: "#2f9b8f",
            boxShadow: "0 10px 24px rgba(20,184,166,0.28)",
        },
        "&.Mui-disabled": {
            color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)",
            borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)",
            backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
        },
    };

    const baaTableSx = {
        "& .MuiTableContainer-root": {
            borderRadius: "12px",
            border: "none",
            backgroundColor: "transparent",
        },
        "& table": {
            
            borderCollapse: "collapse",
            borderSpacing: 0,
        },
        "& .MuiTableCell-root": {
            borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
            py: 1.15,
            fontSize: "0.84rem",
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
            height: "42px",
        },
        "& .MuiTableHead-root .MuiTypography-root": {
            color: "#00B8D9 !important",
            fontSize: "0.78rem",
            fontWeight: 800,
            letterSpacing: "0.02em",
        },
        "& .MuiTableBody-root .MuiTableRow-root": {
            transition: "background-color 0.18s ease, box-shadow 0.18s ease",
        },
        "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: `${baaHoverBg} !important`,
        },
        "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
            color: modalTokens.textPrimary,
            fontSize: "0.84rem",
        },
        "& .MuiIconButton-root": {
            color: isDark ? "#94a3b8" : "#475569",
            borderRadius: "8px",
            "&:hover": {
                color: modalTokens.accent,
                backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
            },
        },
        "& .MuiPaginationItem-root": {
            borderColor: baaBorderColor,
            color: modalTokens.textSecondary,
            borderRadius: "8px",
            "&.Mui-selected": {
                color: "#fff",
                backgroundColor: modalTokens.accent,
                borderColor: modalTokens.accent,
            },
        },
    };
    // -- End design tokens -----------------------------------------------------

    return <>
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                px: { xs: 1, md: 3 },
                py: 2,
            }}
        >
            {showPDF && <PDFViewer
                isOpen={showPDF.isShow}
                onClose={() => setShowPDF({isShow: false, row: null})}
                title={"Business Associate Agreement"}
                base64String={ document?.content || "" }
                fileName={"Business Associate Agreement" + userInfo.userId}
            />}

            <Box>
                <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
                    <Card sx={filterPanelSx}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid item md={3} xs={12}>
                                    <CustomDropdownField name="userRoleId" onChangeSelect={handleUserTypeChange} control={control} label="User Type" placeholderText="Select user type" options={userTypesList} disabled={userTypesList && userTypesList.length === 1} sortOptions={false} />
                                </Grid>
                                {(selectedRoleEntity && loggedInRoleEntity && selectedRoleEntity !== loggedInRoleEntity) ? (
                                    <>
                                        {selectedRoleEntity === AGENCY_ENTITY &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="agencyIds" label="Agency" placeholderText="Select agency" options={agencyList} control={control} size="small" disabled={agencyList && agencyList.length === 1} />
                                            </Grid>
                                        }
                                        {selectedRoleEntity === BILLER_ENTITY &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="billerIds" label="Biller" placeholderText="Select biller" options={billerUsersList} control={control} size="small" disabled={billerUsersList && billerUsersList.length === 1} />
                                            </Grid>
                                        }
                                        {selectedRoleEntity === MANUFACTURER_ENTITY &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="manufactureIds" label="Manufacturer" placeholderText="Select manufacturer" options={manufacturersList} control={control} size="small" disabled={manufacturersList && manufacturersList.length === 1} />
                                            </Grid>
                                        }
                                        {selectedRoleEntity === PRACTICE_ENTITY &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="practiceIds" label="Practice" placeholderText="Select practice" options={practiceOptions} control={control} size="small" disabled={practiceOptions && practiceOptions.length === 1} />
                                            </Grid>
                                        }
                                    </>
                                ) : null}
                                {(((selectedRoleEntity && !loggedInRoleEntity) || (!selectedRoleEntity && loggedInRoleEntity))
                                    || (selectedRoleEntity && loggedInRoleEntity && selectedRoleEntity === loggedInRoleEntity)) ? (
                                    <>
                                        {(loggedInRoleEntity === AGENCY_ENTITY || selectedRoleEntity === AGENCY_ENTITY) &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="agencyIds" label="Agency" placeholderText="Select agency" options={agencyList} control={control} size="small" disabled={agencyList && agencyList.length === 1} />
                                            </Grid>
                                        }
                                        {(loggedInRoleEntity === BILLER_ENTITY || selectedRoleEntity === BILLER_ENTITY) &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="billerIds" label="Biller" placeholderText="Select biller" options={billerUsersList} control={control} size="small" disabled={billerUsersList && billerUsersList.length === 1} />
                                            </Grid>
                                        }
                                        {(loggedInRoleEntity === MANUFACTURER_ENTITY || selectedRoleEntity === MANUFACTURER_ENTITY) &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="manufactureIds" label="Manufacturer" placeholderText="Select manufacturer" options={manufacturersList} control={control} size="small" disabled={manufacturersList && manufacturersList.length === 1} />
                                            </Grid>
                                        }
                                        {(loggedInRoleEntity === PRACTICE_ENTITY || selectedRoleEntity === PRACTICE_ENTITY) &&
                                            <Grid item md={3} xs={12}>
                                                <CustomMultipleDropDown name="practiceIds" label="Practice" placeholderText="Select practice" options={practiceOptions} control={control} size="small" disabled={practiceOptions && practiceOptions.length === 1} />
                                            </Grid>
                                        }
                                    </>
                                ) : null}
                                <Grid item md={3} xs={12}>
                                    <CustomTextField controllerName="firstName" label="First Name" placeholder="Enter first name" control={control} size="small" />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <CustomTextField controllerName="lastName" label="Last Name" placeholder="Enter last name" control={control} size="small" />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <CustomTextField controllerName="email" label="Email" placeholder="Enter email" control={control} size="small" /> {/*CS-1696*/}
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <CustomMultipleDropDown name="status" label="Status" placeholderText="Select status" options={statusOptions} control={control} size="small" />
                                </Grid>
                                <Grid item md={2} xs={12}>
                                    <Button fullWidth size="large" startIcon={<SearchOutlined />} sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }} type="submit" removeUpperCase>
                                        Search
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Collapse>
            </Box>

            <Box mt={2}>
                <Card sx={{ ...baaCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
                        <Grid item xs={12} md>
                            <Typography variant='h3' fontWeight={800} color="text.primary">BAA List</Typography>
                        </Grid>
                        <Grid item xs={12} md="auto">
                            <Button variant="outlined" color="info" onClick={toggleFilterPanel} startIcon={<SearchOutlined />} fullWidth sx={outlineBtnSx} removeUpperCase>
                                {showCard ? "Hide Filters" : "Show Filters"}
                            </Button>
                        </Grid>
                        {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                            <>
                                <Grid item xs={12} sm="auto">
                                    <Button color="success" startIcon={<FormatItalicOutlined />} onClick={HandlePracticeBAAs} fullWidth sx={practiceBaaBtnSx} removeUpperCase>
                                        Practice BAA(s)
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <Button color="warning" startIcon={<AppRegistrationOutlined />} onClick={handleUserBAAs} fullWidth sx={userBaaBtnSx} removeUpperCase>
                                        User BAA(s)
                                    </Button>
                                </Grid>
                            </>
                        )}
                        {/* CS-1685 - Start */}
                        {canExportBaa && (
                            <Grid item xs={12} md="auto">
                                {!isMobile && (
                                    <Button
                                        id="customized-button"
                                        aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                                        variant="outlined"
                                        disableElevation
                                        onClick={handleMenuClick}
                                        startIcon={<ExitToApp />}
                                        sx={exportBtnSx}
                                        removeUpperCase
                                        endIcon={
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "flex",
                                                    transition: "transform 0.3s ease",
                                                    transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                                                    padding: "2px",
                                                }}
                                            >
                                                <KeyboardArrowDown />
                                            </Box>
                                        }
                                    >
                                        Export
                                    </Button>
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12} md="auto">
                            <Box>
                                {canExportBaa && isMobile && (
                                    <Box mt={1}>
                                        <Button
                                            color="success"
                                            variant="text"
                                            onClick={(event) => handleMenuClick(event)}
                                            startIcon={<DownloadOutlined fontSize="small" />}
                                            sx={exportBtnSx}
                                            removeUpperCase
                                        >
                                            Export
                                        </Button>
                                    </Box>
                                )}

                                <StyledMenu
                                    id="customized-menu"
                                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                                    anchorEl={anchorElMenu}
                                    open={Boolean(anchorElMenu)}
                                    onClose={handleMenuClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: "260px",
                                                maxWidth: "90vw",
                                                mt: 0.75,
                                                p: 0.75,
                                                borderRadius: "12px",
                                                border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                                                backgroundColor: isDark ? "#141720" : "#ffffff",
                                                backgroundImage: "none",
                                                boxShadow: isDark
                                                    ? "0 18px 42px rgba(0,0,0,0.45)"
                                                    : "0 14px 32px rgba(15,23,42,0.14)",
                                            },
                                        },
                                    }}
                                >
                                    <Box sx={{ px: 1, mb: 1 }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<CloudUpload />}
                                            onClick={() => handleExport("searchList")}
                                            disabled={totalRecords > 1000}
                                            sx={exportMenuItemBtnSx}
                                            removeUpperCase
                                        >
                                            Export Search List
                                        </Button>
                                    </Box>

                                    {totalRecords > 1000 && (
                                        <Box
                                            sx={{
                                                mx: 1,
                                                p: 1.5,
                                                mt: 1,
                                                backgroundColor: "warning.light",
                                                borderRadius: 1,
                                                color: "warning.contrastText",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                Export is available only when the list contains 1,000 records or fewer.
                                                Maximum export size: 1000 records.
                                            </Typography>
                                        </Box>
                                    )}
                                </StyledMenu>
                            </Box>
                        </Grid>
                        {/* CS-1685 - End */}
                    </Grid>

                    <Table
                        columns={columns}
                        rows={(fetchInitDataCompleted && baaRows) || []}
                        pageNumber={handlePagination}
                        totalPages={fetchInitDataCompleted ? baaList?.totalPages : 0}
                        actionComponent={actionComponent}
                        totalElementText={"Records: " + ((fetchInitDataCompleted && baaList?.totalElements) ? baaList?.totalElements : "0")}
                        currentPageNumber={searchCriteria.currentPage - 1}
                        emptyText="No Business Association Agreement(s) Found"
                        showMenu={true}
                        sortableColumns={baaListSort.sortableColumns}
                        serverSideSorting
                        onSortChange={handleTableSort}
                        controlledSortColumn={baaListSort.controlledColumn(searchCriteria.sortBy)}
                        controlledSortOrder={searchCriteria.sortOrder ?? null}
                        // CS-1555 - Start
                        onRowClick={handleRowNavigateToPdf}
                        clickableCards={true}
                        uniqueValue="baaDetailId"
                        currentPageKey="baaList"
                        activeButtonId={activeButtonId}
                        // CS-1555 - End
                        sx={baaTableSx}
                    />
                </Card>
            </Box>
        </Box>
    </>
}

export default BaaList;